import { useParams, useLocation } from 'react-router-dom'
import { useContext, useState, useEffect } from 'react'
import { APIContext } from 'context/APIContext'
import { ModalKey, ModalContext } from "components/Modals/ModalContext"
import { CapitalizeFirstLetter, GetProgressionLevelName } from "functions/textUtils"
import moment from "moment"

import ScreenFrame from 'components/Frame/ScreenFrame'
import KinesisProfileBreadCrumbs from 'components/Profile/KinesisProfileBreadCrumbs'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import ContentFrame from 'components/Frame/ContentFrame'

export default function FitnessProfileView({ propProfileType = 'individual' }) {

    const { urlGroupId, urlAthleteId } = useParams()
    const location = useLocation()

    const { GetGroupFitnessProfile, GetIndividualFitnessProfile } = useContext(APIContext)
    const { ShowModal } = useContext(ModalContext)

    const [fitnessProfile, setFitnessProfile] = useState(null)

    const LoadFitnessProfile = async () => {

        setFitnessProfile(null)
        
        var fitnessProfile = null

        if (propProfileType === 'group') {
            fitnessProfile = await GetGroupFitnessProfile(urlGroupId)
        }

        if (propProfileType === 'individual') {
            fitnessProfile = await GetIndividualFitnessProfile(urlAthleteId)
        }

        setFitnessProfile(fitnessProfile)

    }

    const OnEditFitnessProfile = () => {
        const modalData = {
            key: ModalKey.EditFitnessProfile,
            size: 'half',
            profileType: propProfileType,
            groupId: urlGroupId,
            userId: urlAthleteId,
            fitnessProfile: fitnessProfile,
            boilerplateEnabled: 1
        }

        ShowModal(modalData)
    }

    useEffect(() => { LoadFitnessProfile() }, [propProfileType, urlGroupId, urlAthleteId])

    if (fitnessProfile === null) {
        return (
            <ScreenFrame>
                <div className='max-w-2xl mx-auto space-y-4'>
                    <div className='flex-shrink-0 h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='flex-shrink-0 h-[400px] bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='max-w-2xl mx-auto space-y-4'>
                <KinesisProfileBreadCrumbs 
                    propProfileType={propProfileType} 
                    profileName={location.state?.profileName} 
                    currentPageName='Fitness Profile'
                />

                <div className='flex items-center gap-x-4 pb-1'>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                        Fitness Profile
                    </h1>

                    <div className="w-32">
                        <BrandColorButton
                            size='extra-small'
                            text="Edit"
                            action={() => OnEditFitnessProfile()}
                        />
                    </div>
                </div>

                {fitnessProfile.id === null && (
                    <ContentFrame>
                        <div className="text-center text-gray-500 py-4">No fitness profile found</div>
                    </ContentFrame>
                )}

                {fitnessProfile.id !== null && (
                    <>
                        {propProfileType === 'group' && (
                            <ContentFrame>
                                <div className="w-full px-8 py-6 space-y-4">
                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Average Group Age</div>
                                        <div>{fitnessProfile.defaultAverageAge} years old</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Default Group Biological Gender</div>
                                        <div>{CapitalizeFirstLetter(fitnessProfile.defaultGender)}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Measurement System</div>
                                        <div>{CapitalizeFirstLetter(fitnessProfile.measurementSystem)}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Average Group Height</div>
                                        <div>{Math.floor(fitnessProfile.defaultAverageHeight / 12)}' {fitnessProfile.defaultAverageHeight % 12}"</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Average Group Weight</div>
                                        <div>{fitnessProfile.defaultAverageWeight} lbs</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Sport</div>
                                        <div>{fitnessProfile.sportName}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Average Training Days Per Week</div>
                                        <div>{fitnessProfile.daysOfTraining} day{fitnessProfile.daysOfTraining !== 1 ? 's' : ''}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Months Trained Over the Past Year</div>
                                        <div>{fitnessProfile.monthsOfTraining} month{fitnessProfile.monthsOfTraining !== 1 ? 's' : ''}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Years of Strength Training</div>
                                        <div>{fitnessProfile.yearsOfTraining} year{fitnessProfile.yearsOfTraining !== 1 ? 's' : ''}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Progression Level</div>
                                        <div>{GetProgressionLevelName(fitnessProfile.progressionLevel)}</div>
                                    </div>
                                </div>
                            </ContentFrame>
                        )}

                        {propProfileType === 'individual' && (
                            <ContentFrame>
                                <div className="w-full px-8 py-6 space-y-4">
                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Date of Birth</div>
                                        <div>{moment(fitnessProfile.birthday).format('MMMM D, YYYY')}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Biological Gender</div>
                                        <div>{CapitalizeFirstLetter(fitnessProfile.gender)}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Measurement System</div>
                                        <div>{CapitalizeFirstLetter(fitnessProfile.measurementSystem)}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Height</div>
                                        <div>{Math.floor(fitnessProfile.height / 12)}' {fitnessProfile.height % 12}"</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Weight</div>
                                        <div>{fitnessProfile.weight} lbs</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Sport</div>
                                        <div>{fitnessProfile.sportName}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Average Training Days Per Week</div>
                                        <div>{fitnessProfile.daysOfTraining} day{fitnessProfile.daysOfTraining !== 1 ? 's' : ''}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Months Trained Over the Past Year</div>
                                        <div>{fitnessProfile.monthsOfTraining} month{fitnessProfile.monthsOfTraining !== 1 ? 's' : ''}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Years of Strength Training</div>
                                        <div>{fitnessProfile.yearsOfTraining} year{fitnessProfile.yearsOfTraining !== 1 ? 's' : ''}</div>
                                    </div>

                                    <div className="flex text-gray-500">
                                        <div className="flex-grow font-semibold">Progression Level</div>
                                        <div>{GetProgressionLevelName(fitnessProfile.progressionLevel)}</div>
                                    </div>
                                </div>
                            </ContentFrame>
                        )}
                    </>
                )}
            </div>
        </ScreenFrame>
    )
}